import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { EmptyState, showToast } from "../primitives";
import { Panel, PanelRow, PANEL_COL_ICON, PANEL_COL_TIME } from "./Panel";
import { NewPromptModal } from "../scope/NewPromptModal";
import { PromptArgsModal } from "../scope/PromptArgsModal";
import { PromptDisplayModal } from "../scope/PromptDisplayModal";
import { humanizePromptName } from "../command-palette/format";
import { useListPromptsInScope } from "../../hooks/use-list-prompts-in-scope";
import { useIsOwner } from "../../hooks/use-is-owner";
import {
  useArchiveNodeAction,
  useRestoreNodeAction,
  useDeleteNodeAction,
} from "../../hooks/use-node-actions";
import { PanelRowMenu } from "./PanelRowMenu";
import { ConfirmDeleteRowModal } from "./ConfirmDeleteRowModal";
import { ArchivedBadge, ShowArchivedChip, useShowArchived } from "./panel-archive";
import type { McpPrompt } from "../command-palette/types";
import type { ScopedPrompt } from "../../../lib/types";

interface PromptsPanelProps {
  nodeId: string;
  /** Display name of the scope, for the "scoped to …" empty hint + new modal. */
  nodeName?: string;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

/** ScopedPrompt → the McpPrompt shape the Sprint 3 args/display modals consume. */
function toMcpPrompt(p: ScopedPrompt): McpPrompt {
  return {
    name: p.name,
    description: p.description,
    arguments: p.arguments.map((a) => ({
      name: a.name,
      ...(a.description ? { description: a.description } : {}),
      ...(a.required ? { required: true } : {}),
    })),
  };
}

/**
 * Sprint 11 (S11.42) — cockpit Prompts panel. Lists the prompts visible from
 * this scope (authored here or inherited from an ancestor). Each row runs via
 * the reused Sprint 3 PromptArgsModal → PromptDisplayModal flow, or opens the
 * editor / archives via the action menu. Sits in the Documents group after Pages.
 */
export function PromptsPanel({ nodeId, nodeName }: PromptsPanelProps) {
  const [showArchived, setShowArchived] = useShowArchived("prompts", nodeId);
  const prompts = useListPromptsInScope(nodeId, showArchived);

  const [newOpen, setNewOpen] = useState(false);
  const [argsFor, setArgsFor] = useState<McpPrompt | null>(null);
  const [display, setDisplay] = useState<{ prompt: McpPrompt; args: Record<string, string> } | null>(null);

  const items = prompts.data ?? [];

  function run(p: ScopedPrompt) {
    const mcp = toMcpPrompt(p);
    const hasArgs = (mcp.arguments?.length ?? 0) > 0;
    if (hasArgs) {
      setArgsFor(mcp);
    } else {
      setDisplay({ prompt: mcp, args: {} });
    }
  }

  return (
    <>
      <Panel
        title="Prompts"
        count={items.length}
        isLoading={prompts.isLoading}
        error={prompts.error}
        onRetry={() => prompts.refetch()}
        isEmpty={items.length === 0}
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ShowArchivedChip value={showArchived} onChange={setShowArchived} />
            <button onClick={() => setNewOpen(true)} style={newBtn} aria-label="New prompt">
              <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 3 }} aria-hidden="true" /> New
            </button>
          </div>
        }
        empty={
          showArchived ? (
            <EmptyState>No archived items. Items you archive will appear here for restore.</EmptyState>
          ) : (
            <EmptyState>
              No prompts here yet. Prompts are invocable templates — author one to encode a repeatable
              workflow{nodeName ? ` for ${nodeName}` : ""}.
            </EmptyState>
          )
        }
      >
        {items.map((p) => (
          <PromptRow key={p.id} prompt={p} onRun={() => run(p)} />
        ))}
      </Panel>

      <NewPromptModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        parentId={nodeId}
        parentName={nodeName}
      />

      <PromptArgsModal
        open={!!argsFor}
        prompt={argsFor}
        onClose={() => setArgsFor(null)}
        onSubmit={(args) => {
          if (argsFor) setDisplay({ prompt: argsFor, args });
          setArgsFor(null);
        }}
      />

      <PromptDisplayModal
        open={!!display}
        onClose={() => setDisplay(null)}
        title={display ? humanizePromptName(display.prompt.name) : ""}
        subtitle={display?.prompt.description}
        icon="bolt"
        promptName={display?.prompt.name ?? ""}
        args={display?.args ?? {}}
      />
    </>
  );
}

function PromptRow({ prompt, onRun }: { prompt: ScopedPrompt; onRun: () => void }) {
  const navigate = useNavigate();
  const archiveNode = useArchiveNodeAction();
  const restoreNode = useRestoreNodeAction();
  const deleteNode = useDeleteNodeAction();
  const canDelete = useIsOwner();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isArchived = !!prompt.archived_at;
  const displayName = humanizePromptName(prompt.name);

  function handleArchive() {
    archiveNode.mutate(
      { id: prompt.id },
      {
        onSuccess: () =>
          showToast(`Archived '${displayName}'`, "success", false, {
            label: "Undo",
            onClick: () => restoreNode.mutate({ id: prompt.id }),
          }, 10000),
        onError: (e) => showToast(e instanceof Error ? e.message : "Archive failed", "error", true),
      },
    );
  }

  function handleRestore() {
    restoreNode.mutate(
      { id: prompt.id },
      {
        onSuccess: () => showToast(`Restored '${displayName}'`),
        onError: (e) => showToast(e instanceof Error ? e.message : "Restore failed", "error", true),
      },
    );
  }

  return (
    <>
      <PanelRow
        onClick={() => navigate(`/library/${prompt.id}`)}
        title={`Open ${prompt.name}`}
        columns={[
          {
            key: "icon",
            width: PANEL_COL_ICON,
            content: <i className="ti ti-bolt" style={{ fontSize: 15, color: "var(--c-amber-500, #f59e0b)", opacity: isArchived ? 0.6 : 1 }} aria-hidden="true" />,
          },
          {
            key: "main",
            flex: true,
            content: (
              <div style={{ minWidth: 0, opacity: isArchived ? 0.6 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={nameStyle}>{displayName}</span>
                  {isArchived && <ArchivedBadge />}
                </div>
                {prompt.description ? <div style={descStyle}>{prompt.description}</div> : null}
              </div>
            ),
          },
          {
            key: "time",
            width: PANEL_COL_TIME,
            align: "right",
            content: <span style={timeStyle}>{relativeTime(prompt.updated_at)}</span>,
          },
        ]}
        trailing={
          <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
            {!isArchived && (
              <button onClick={onRun} style={runBtn} aria-label={`Run ${prompt.name}`}>
                <i className="ti ti-player-play" style={{ fontSize: 12, marginRight: 3 }} aria-hidden="true" /> Run
              </button>
            )}
            <PanelRowMenu
              isArchived={isArchived}
              canDelete={canDelete}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onDelete={() => setConfirmDelete(true)}
              extraItems={[{ label: "Edit", icon: "pencil", onClick: () => navigate(`/library/${prompt.id}`) }]}
            />
          </div>
        }
      />
      <ConfirmDeleteRowModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        entityKind="prompt"
        entityName={displayName}
        entityId={prompt.id}
        backRefKind="node"
        onConfirm={async ({ force }) => {
          await deleteNode.mutateAsync({ id: prompt.id, force });
          showToast(`Deleted '${displayName}'`);
        }}
      />
    </>
  );
}

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const descStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const timeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};
const newBtn: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
};
const runBtn: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "4px 9px",
  display: "inline-flex",
  alignItems: "center",
  whiteSpace: "nowrap",
};
